import { motion, AnimatePresence } from 'framer-motion'
import { X, Check, ChevronRight, ThumbsUp, MessageCircle, Gift, Smile, Share2, UserPlus, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { templateConfigService, type TemplateOption, type StyleOption, type InteractivityOption } from '../../services/templateConfigService'
import { projectsService } from '../../services/projectsService'
import { useProjectsStore } from '../../store/useProjectsStore'
import { useAnalytics } from '../../hooks/useAnalytics'

// Import template images for fallback/display
import styleBeemi from '../../assets/templates/style-beemi.png'
import styleSimple from '../../assets/templates/style-simple.png'
import styleDoodle from '../../assets/templates/style-doodle.png'
import styleNeon from '../../assets/templates/style-neon.png'
import styleRetro from '../../assets/templates/style-retro.png'

// Import game template images
import gameNone from '../../assets/templates/game-none.png'
import gameTicTacToe from '../../assets/templates/game-tic_tac_toe.png'
import gameRockPaperScissors from '../../assets/templates/game-rock_paper_scissors.png'
import gameCoinFlip from '../../assets/templates/game-coin_flip.png'
import { FILL_GRADIENT, LINE_GRADIENT } from '../../constants/gradients'

interface CreateGameModalProps {
  isOpen: boolean
  onClose: () => void
  initialTemplate?: string  // Pre-select a template and skip to step 2
  initialTab?: 'prompt' | 'template'  // Which tab to show on step 1
}

// Icon mapping for interactivity options
const iconMap: Record<string, any> = {
  likes: ThumbsUp,
  comments: MessageCircle,
  gifts: Gift,
  emoji: Smile,
  shares: Share2,
  follows: UserPlus,
  joins: UserPlus
}

// Image mapping for templates and styles
// NOTE: Images are currently stored locally in src/assets/templates/
// When adding a new template to the server config, you must also:
// 1. Add the image file to src/assets/templates/
// 2. Import it at the top of this file
// 3. Add it to these mapping objects
// TODO: Consider serving images from server for full centralization
const templateImageMap: Record<string, any> = {
  blank: gameNone,
  tictactoe: gameTicTacToe,
  rockpaperscissors: gameRockPaperScissors,
  coinflip: gameCoinFlip,
  trivia: gameNone,
  poll: gameNone
}

const styleImageMap: Record<string, any> = {
  pixel: styleRetro,
  neon: styleNeon,
  fantasy: styleDoodle,
  modern: styleSimple,
  cartoon: styleDoodle,
  beemi: styleBeemi
}

const CreateGameModal = ({ isOpen, onClose, initialTemplate, initialTab }: CreateGameModalProps) => {
  const navigate = useNavigate()
  const { fetchProjects } = useProjectsStore()
  const { trackModal, trackEvent } = useAnalytics()
  const [currentStep, setCurrentStep] = useState(1)
  const [activeTab, setActiveTab] = useState<'prompt' | 'template'>('prompt')
  const [promptText, setPromptText] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null)
  const [selectedInteractivity, setSelectedInteractivity] = useState<string[]>([])
  const [isCreating, setIsCreating] = useState(false)
  
  // Dynamic config from server
  const [templates, setTemplates] = useState<TemplateOption[]>([])
  const [themes, setThemes] = useState<StyleOption[]>([])
  const [interactivityOptions, setInteractivityOptions] = useState<InteractivityOption[]>([])
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  
  // Track modal open/close
  useEffect(() => {
    if (isOpen) {
      trackModal('opened', 'create_game', {
        initial_template: initialTemplate,
        initial_tab: initialTab
      });
    }
  }, [isOpen])

  // Fetch template configuration from server
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setIsLoadingConfig(true)
        const config = await templateConfigService.getConfig()
        setTemplates(config.templates)
        setThemes(config.styles)
        setInteractivityOptions(config.interactivity)
        console.log('✅ Template config loaded in modal')
      } catch (error) {
        console.error('❌ Failed to load template config:', error)
      } finally {
        setIsLoadingConfig(false)
      }
    }
    
    fetchConfig()
  }, [])

  // Handle initial template selection (skip to step 2)
  useEffect(() => {
    if (isOpen && initialTemplate) {
      console.log('🎯 Pre-selecting template:', initialTemplate)
      setSelectedTemplate(initialTemplate)
      setActiveTab('template')
      setCurrentStep(2) // Skip to theme selection
    } else if (isOpen && initialTab) {
      // Just set the initial tab without skipping steps
      setActiveTab(initialTab)
    }
  }, [isOpen, initialTemplate, initialTab])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setCurrentStep(1)
        setActiveTab('prompt')
        setPromptText('')
        setSelectedTemplate(null)
        setSelectedTheme(null)
        setSelectedInteractivity([])
      }, 300)
    }
  }, [isOpen])

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const handleNext = () => {
    if (currentStep === 1 && (promptText.trim() || selectedTemplate)) {
      setCurrentStep(2)
    } else if (currentStep === 2 && selectedTheme) {
      setCurrentStep(3)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const toggleInteractivity = (id: string) => {
    setSelectedInteractivity(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    )
  }

  const handleCreate = async () => {
    if (!(promptText.trim() || selectedTemplate) || !selectedTheme || selectedInteractivity.length === 0) {
      return
    }

    try {
      setIsCreating(true)
      
      // Detect if this is a custom prompt or template selection
      const isCustomPrompt = !!promptText.trim()
      const customPrompt = isCustomPrompt ? promptText.trim() : null
      const template = isCustomPrompt ? null : (selectedTemplate || 'blank')
      
      // Name base for the game
      const nameBase = (customPrompt || template || 'Game').substring(0, 30)
      
      console.log('Creating game with:', { 
        template, 
        prompt: customPrompt,
        style: selectedTheme, 
        interactivity: selectedInteractivity
      })
      
      // Track project creation start
      trackEvent('project_created', {
        creation_method: isCustomPrompt ? 'prompt' : (template === 'blank' ? 'blank' : 'template'),
        template_id: template,
        has_prompt: !!customPrompt,
        prompt_length: customPrompt?.length || 0,
        selected_style: selectedTheme,
        selected_interactivity: selectedInteractivity
      });
      
      const newProject = await projectsService.createProject({
        name: `New ${nameBase} Game`,
        template,          // Template ID (e.g., 'trivia', 'blank') OR null
        prompt: customPrompt,  // Custom prompt text OR null
        style: selectedTheme,
        interactivity: selectedInteractivity
      })
      
      console.log('✅ Project created, refreshing projects list and navigating to editor:', newProject.id)
      
      // Refresh projects list so it appears in the projects page/dashboard
      fetchProjects().catch(err => console.warn('Failed to refresh projects:', err))
      
      onClose()
      navigate(`/editor/${newProject.id}`)
    } catch (error) {
      console.error('Failed to create project:', error)
      alert('Failed to create project. Please try again.')
      setIsCreating(false)
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col relative"
            >
              {/* Gradient Line at Top */}
              <div className="h-1 w-full" style={{ background: FILL_GRADIENT }} />
              {/* Header */}
              <div className="flex items-center justify-between p-6">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text">
                    {currentStep === 1 ? 'Choose Template' : currentStep === 2 ? 'Choose Your Game Theme' : 'Select Livestream interactivity'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">
                    {currentStep === 1 ? 'Select a starting template for your game' : currentStep === 2 ? 'Customize the visual style for your game' : 'Enable ways for your audience to interact with the game during livestreams'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pb-6 overflow-y-auto h-[600px]">
                {/* Step 1: Choose Template or Prompt */}
                {currentStep === 1 && (
                  <div>
                    {/* Tabs */}
                    <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 mb-6">
                      <button
                        onClick={() => {
                          setActiveTab('prompt')
                          setSelectedTemplate(null) // Clear template selection when switching to prompt
                        }}
                        className={`px-4 py-2 font-semibold text-sm transition-all duration-200 relative ${
                          activeTab === 'prompt'
                            ? 'text-gray-900 dark:text-dark-text'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        Prompt
                        {activeTab === 'prompt' && (
                          <div 
                            className="absolute bottom-0 left-0 right-0 h-0.5"
                            style={{ background: LINE_GRADIENT }}
                          />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('template')
                          setPromptText('') // Clear prompt text when switching to template
                        }}
                        className={`px-4 py-2 font-semibold text-sm transition-all duration-200 relative ${
                          activeTab === 'template'
                            ? 'text-gray-900 dark:text-dark-text'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                      >
                        Template
                        {activeTab === 'template' && (
                          <div 
                            className="absolute bottom-0 left-0 right-0 h-0.5"
                            style={{ background: LINE_GRADIENT }}
                          />
                        )}
                      </button>
                    </div>

                    {/* Prompt Tab Content */}
                    {activeTab === 'prompt' && (
                      <div className="pt-3">
                        <textarea
                          value={promptText}
                          onChange={(e) => setPromptText(e.target.value)}
                          placeholder="Create a memory tile game where viewers can guess the tile that's about to be revealed. Each correct guess earns points, and the player with the most points wins..."
                          className="w-full h-[400px] p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-dark-text placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 transition-colors"
                        />
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                          Describe your game idea in detail. Our AI will generate a custom game based on your prompt.
                        </p>
                      </div>
                    )}

                    {/* Template Tab Content */}
                    {activeTab === 'template' && (
                      <div>
                        {isLoadingConfig ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-4 pt-3">
                            {templates.map((template) => {
                              const templateImage = templateImageMap[template.id] || gameNone
                              
                              return (
                                <button
                                  key={template.id}
                                  onClick={() => setSelectedTemplate(template.id)}
                                  className={`relative group transition-all duration-300 ease-out ${
                                    selectedTemplate === template.id ? 'scale-105' : 'scale-100'
                                  }`}
                                >
                                  <div className={`relative aspect-square rounded-2xl overflow-hidden transition-all duration-300 ease-out ${
                                    selectedTemplate === template.id
                                      ? 'p-1 shadow-lg'
                                      : 'border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                  }`}
                                  style={selectedTemplate === template.id ? { background: LINE_GRADIENT } : undefined}
                                  >
                                    <div className="relative w-full h-full rounded-[12px] overflow-hidden">
                                      <img 
                                        src={templateImage} 
                                        alt={template.name}
                                        className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out ${
                                          selectedTemplate === template.id ? 'scale-110' : 'scale-100'
                                        }`}
                                      />
                                      {/* Checkmark */}
                                      <div className={`absolute top-3 right-3 w-8 h-8 bg-white dark:bg-gray-900 rounded-full flex items-center justify-center shadow-lg z-10 transition-all duration-300 ease-out ${
                                        selectedTemplate === template.id ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                                      }`}>
                                        <Check className="w-5 h-5 text-pink-500" strokeWidth={3} />
                                      </div>
                                    </div>
                                  </div>
                                  <p className="mt-3 text-center text-sm font-semibold text-gray-900 dark:text-dark-text">
                                    {template.name}
                                  </p>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Choose Theme */}
                {currentStep === 2 && (
                  <div>
                    {isLoadingConfig ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4 pt-3">
                        {themes.map((theme) => {
                          const themeImage = styleImageMap[theme.id] || styleSimple
                          
                          return (
                            <button
                              key={theme.id}
                              onClick={() => setSelectedTheme(theme.id)}
                              className={`relative group transition-all duration-300 ease-out ${
                                selectedTheme === theme.id ? 'scale-105' : 'scale-100'
                              }`}
                            >
                              <div className={`relative aspect-square rounded-2xl overflow-hidden transition-all duration-300 ease-out ${
                                selectedTheme === theme.id
                                  ? 'p-1 shadow-lg'
                                  : 'border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }`}
                              style={selectedTheme === theme.id ? { background: LINE_GRADIENT } : undefined}
                              >
                                <div className="relative w-full h-full rounded-[12px] overflow-hidden">
                                  <img 
                                    src={themeImage} 
                                    alt={theme.name}
                                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out ${
                                      selectedTheme === theme.id ? 'scale-110' : 'scale-100'
                                    }`}
                                  />
                                  {/* Checkmark */}
                                  <div className={`absolute top-3 right-3 w-8 h-8 bg-white dark:bg-gray-900 rounded-full flex items-center justify-center shadow-lg z-10 transition-all duration-300 ease-out ${
                                    selectedTheme === theme.id ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                                  }`}>
                                    <Check className="w-5 h-5 text-pink-500" strokeWidth={3} />
                                  </div>
                                </div>
                              </div>
                              <p className="mt-3 text-center text-sm font-semibold text-gray-900 dark:text-dark-text">
                                {theme.name}
                              </p>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Select Interactivity */}
                {currentStep === 3 && (
                  <div>
                    {isLoadingConfig ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-4 pt-3">
                        {interactivityOptions.map((option) => {
                          const Icon = iconMap[option.id] || MessageCircle
                          const isSelected = selectedInteractivity.includes(option.id)
                          
                          return (
                            <button
                              key={option.id}
                              onClick={() => toggleInteractivity(option.id)}
                              className={`relative group transition-all duration-300 ease-out ${
                                isSelected ? 'scale-105' : 'scale-100'
                              }`}
                            >
                              <div className={`relative aspect-square rounded-2xl overflow-hidden transition-all duration-300 ease-out ${
                                isSelected
                                  ? 'p-1 shadow-lg'
                                  : 'border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }`}
                              style={isSelected ? { background: LINE_GRADIENT } : undefined}
                              >
                                <div className="relative w-full h-full rounded-[12px] overflow-hidden bg-white dark:bg-gray-900">
                                  <div className={`absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out ${
                                    isSelected ? 'scale-110' : 'scale-100'
                                  }`}>
                                    <div 
                                      className="w-24 h-24 rounded-full flex items-center justify-center"
                                      style={isSelected ? { background: FILL_GRADIENT } : { backgroundColor: '#E5E7EB' }}
                                    >
                                      <Icon 
                                        className="w-12 h-12" 
                                        style={{ color: isSelected ? '#FFFFFF' : '#9CA3AF' }}
                                        strokeWidth={2}
                                      />
                                    </div>
                                  </div>
                                  {/* Checkmark */}
                                  <div className={`absolute top-3 right-3 w-8 h-8 bg-white dark:bg-gray-900 rounded-full flex items-center justify-center shadow-lg z-10 transition-all duration-300 ease-out ${
                                    isSelected ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
                                  }`}>
                                    <Check className="w-5 h-5 text-pink-500" strokeWidth={3} />
                                  </div>
                                </div>
                              </div>
                              <p className="mt-3 text-center text-sm font-semibold text-gray-900 dark:text-dark-text">
                                {option.name}
                              </p>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-dark-border">
                <p className="text-sm text-gray-500 dark:text-dark-text-muted">
                  Step {currentStep} of 3
                </p>
                
                <div className="flex gap-3">
                  {currentStep > 1 && (
                    <button
                      onClick={handleBack}
                      className="px-6 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Back
                    </button>
                  )}
                  
                  {currentStep < 3 ? (
                    <button
                      onClick={handleNext}
                      disabled={(currentStep === 1 && !promptText.trim() && !selectedTemplate) || (currentStep === 2 && !selectedTheme)}
                      className={`px-6 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 flex items-center gap-2 ${
                        (currentStep === 1 && !promptText.trim() && !selectedTemplate) || (currentStep === 2 && !selectedTheme)
                          ? 'opacity-50'
                          : 'hover:shadow-lg hover:scale-105 active:scale-95'
                      }`}
                      style={(currentStep === 1 && (promptText.trim() || selectedTemplate)) || (currentStep === 2 && selectedTheme) ? { background: FILL_GRADIENT } : { background: '#9CA3AF' }}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleCreate}
                      disabled={(!promptText.trim() && !selectedTemplate) || !selectedTheme || selectedInteractivity.length === 0 || isCreating}
                      className={`px-6 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 flex items-center gap-2 ${
                        (!promptText.trim() && !selectedTemplate) || !selectedTheme || selectedInteractivity.length === 0 || isCreating
                          ? 'opacity-50'
                          : 'hover:shadow-lg hover:scale-105 active:scale-95'
                      }`}
                      style={(promptText.trim() || selectedTemplate) && selectedTheme && selectedInteractivity.length > 0 && !isCreating ? { background: FILL_GRADIENT } : { background: '#9CA3AF' }}
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Game'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CreateGameModal

